import { SUPPORTED_LOCALES } from '@syncanix/shared';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@syncanix/ui';
import { useId } from 'react';

import { useT } from '@/core/providers/I18nProvider';

import type { Locale } from '@syncanix/shared';
import type { ReactElement } from 'react';

/**
 * The language picker on the Terms-of-Service interstitial (ADR-0497).
 *
 * WHY THE GATE NEEDS ONE AT ALL. Consent has to be informed, and the interstitial
 * is the one screen in the product a user cannot navigate away from. The
 * dashboard's own language switcher lives in the user menu — behind this gate — so
 * a user whose locale was resolved wrongly (a shared machine, a browser sending
 * `en-US` for someone who reads only Hebrew, a first visit with no stored
 * preference) would otherwise be asked to agree to a contract they cannot read,
 * with no way to change it. That is a consent defect, not a convenience gap.
 *
 * It also makes the RECORDED locale true. `user_terms_acceptances.locale` is
 * evidence about how consent was obtained, and the provider reads the LIVE locale
 * when the button is pressed — so switching here changes what is stored, which is
 * the whole point of storing it.
 *
 * NATIVE NAMES, per `ui-design.md` § 16. This is self-SELECTION, not
 * configuration: a Hebrew-only reader has to be able to FIND `עברית` in the list,
 * and an English label would leave them nowhere to go. Each option carries its own
 * `lang` so a screen reader pronounces it in that language rather than spelling it
 * out in the page's.
 *
 * Changing the locale re-renders the whole screen through `I18nProvider`, which
 * also updates `<html lang>` and `<html dir>` — so picking Arabic or Hebrew
 * mirrors the layout immediately.
 */
export function TermsLanguagePicker(): ReactElement {
  const { t, locale, setLocale } = useT();
  const labelId = useId();

  return (
    <div className="flex items-center justify-between gap-3">
      {/* A visible label, not a placeholder (§ 6). It is localized, which is of
          limited help to the very reader who needs this control — which is exactly
          why the OPTIONS are native names: the list is legible even when the label
          is not. */}
      <span id={labelId} className="text-sm text-muted-foreground">
        {t('usermenu.language')}
      </span>
      <Select
        value={locale}
        onValueChange={(next) => {
          setLocale(next as Locale);
        }}
      >
        <SelectTrigger aria-labelledby={labelId} className="w-auto min-w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LOCALES.map((meta) => (
            <SelectItem key={meta.code} value={meta.code}>
              <span lang={meta.code}>{meta.nativeName}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
